import { useContext, useEffect, useState } from "react";

import { LikeableType } from "@every.org/common/src/entity/types";
import {
  likeDonationRouteSpec,
  unlikeDonationRouteSpec,
} from "@every.org/common/src/routes/donation";
import {
  likeFundraiserUpdatePostRouteSpec,
  unlikeFundraiserUpdatePostRouteSpec,
} from "@every.org/common/src/routes/fundraiserUpdatePost";
import {
  likeNonprofitRouteSpec,
  unlikeNonprofitRouteSpec,
} from "@every.org/common/src/routes/nonprofit";
import {
  likeNonprofitTagRouteSpec,
  unlikeNonprofitTagRouteSpec,
} from "@every.org/common/src/routes/tag";

import {
  dispatchLikesAction,
  LikesContext,
  LikeChange,
  LikeIdentificator,
  LikesActionType,
} from "src/context/LikesContext";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

function getToggleLikeApiRouteSpec(
  likeableType: Exclude<LikeableType, LikeableType.UPDATE_POST>,
  actionType: LikesActionType.SUBMIT_LIKE | LikesActionType.SUBMIT_UNLIKE
) {
  switch (likeableType) {
    case LikeableType.DONATION:
      return actionType === LikesActionType.SUBMIT_LIKE
        ? likeDonationRouteSpec
        : unlikeDonationRouteSpec;
    case LikeableType.TAG:
      return actionType === LikesActionType.SUBMIT_LIKE
        ? likeNonprofitTagRouteSpec
        : unlikeNonprofitTagRouteSpec;
    case LikeableType.FUND:
    case LikeableType.NONPROFIT:
      return actionType === LikesActionType.SUBMIT_LIKE
        ? likeNonprofitRouteSpec
        : unlikeNonprofitRouteSpec;
  }
}

export default function useLike(
  initData: LikeIdentificator & {
    loggedInUserLikes: boolean | null;
    likeCount: number | null;
    updatePostLikeRouteTokens?: {
      fundraiserIdentifier: LikeIdentificator["id"];
      nonprofitIdentifier: LikeIdentificator["id"];
    };
  },
  onChange?: (change: LikeChange) => void
) {
  const likesState = useContext(LikesContext);
  const [addedToContext, setAddedToContext] = useState(false);
  const { id, type } = initData;
  const likeData = likesState[type].get(id);

  async function onLike() {
    if (!likeData) {
      return;
    }
    dispatchLikesAction({
      type: LikesActionType.SUBMIT_LIKE,
      data: { id, type, ...likeData },
    });

    try {
      await sendLikeRequest({
        actionType: LikesActionType.SUBMIT_LIKE,
        likeableType: type,
      });
    } catch (err) {
      onChange && onChange(LikeChange.ERROR);
    } finally {
      dispatchLikesAction({
        type: LikesActionType.LIKE_SAVED,
        data: { id, type },
      });

      onChange && onChange(LikeChange.LIKED);
    }
  }

  async function onUnlike() {
    if (!likeData) {
      return;
    }
    dispatchLikesAction({
      type: LikesActionType.SUBMIT_UNLIKE,
      data: { id, type, ...likeData },
    });
    try {
      await sendLikeRequest({
        actionType: LikesActionType.SUBMIT_UNLIKE,
        likeableType: type,
      });
    } catch (err) {
      onChange && onChange(LikeChange.ERROR);
    } finally {
      dispatchLikesAction({
        type: LikesActionType.UNLIKE_SAVED,
        data: { id, type },
      });
      onChange && onChange(LikeChange.UNLIKED);
    }
  }

  async function sendLikeRequest({
    actionType,
    likeableType,
  }: {
    actionType: LikesActionType.SUBMIT_LIKE | LikesActionType.SUBMIT_UNLIKE;
    likeableType: LikeableType;
  }) {
    if (likeableType === LikeableType.UPDATE_POST) {
      if (!initData.updatePostLikeRouteTokens) {
        throw new Error("Missing route tokens for update post like request");
      }

      const updatePostRoute =
        actionType === LikesActionType.SUBMIT_LIKE
          ? likeFundraiserUpdatePostRouteSpec
          : unlikeFundraiserUpdatePostRouteSpec;

      await queryApi(updatePostRoute, {
        routeTokens: {
          ...initData.updatePostLikeRouteTokens,
          updatePostIdentifier: id,
        },
        queryParams: {},
        body: {},
      });
      return;
    }

    const likeRoute = getToggleLikeApiRouteSpec(likeableType, actionType);

    try {
      await queryApi(likeRoute, {
        routeTokens: { id },
        queryParams: {},
        body: {},
      });
    } catch (err) {
      if (err instanceof Error) {
        logger.warn({ message: err.message, data: { id } });
        throw new Error(err.message);
      }
    }
  }

  useEffect(() => {
    if (addedToContext) {
      return;
    }

    if (!likeData) {
      dispatchLikesAction({
        type: LikesActionType.ADD_TO_CONTEXT,
        data: {
          ...initData,
          loggedInUserLikes: initData.loggedInUserLikes || false,
          likeCount: initData.likeCount || 0,
        },
      });
      setAddedToContext(true);
    }
  }, [addedToContext, initData, likeData, likesState]);

  return {
    ...likeData,
    likeCount: likeData?.likeCount || initData.likeCount || 0,
    id,
    type,
    onLike,
    onUnlike,
  };
}
